import { Button, Spinner, useTranslation } from '@hbs-us/ui';
import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { type ChangeEvent, type FormEvent, useEffect, useId, useRef, useState } from 'react';

import { RichTextEditor } from '../../components/RichTextEditorLazy.js';
import { apiFetch } from '../../lib/apiFetch.js';
import { openCredentialed } from '../../lib/download.js';
import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import { useSettingsDefaults } from '../settings/useSettingsDefaults.js';
import {
  buildInvoiceDraftPayload,
  type InvoiceDiscountType,
  InvoiceLineItemsEditor,
  type InvoiceLineRow,
  newInvoiceLineRow,
} from './components/InvoiceLineItemsEditor.js';
import { invoiceAttachmentUrl } from './exportLinks.js';
import {
  INVOICE_ATTACHMENT_ACCEPT,
  invoiceQueryKey,
  uploadInvoiceAttachment,
  useDeleteInvoiceAttachmentMutation,
  useInvoiceQuery,
  useUploadInvoiceAttachmentMutation,
  validateInvoiceAttachmentFile,
} from './useInvoiceQuery.js';
import { useJobsQuery } from './useJobsQuery.js';
import { useNextInvoiceNumberQuery } from './useNextInvoiceNumberQuery.js';

/**
 * Create/edit invoice form (legacy `AddInvoicePage` / `EditInvoicePage`),
 * re-skinned to the locked brand and shaped to the api-host JSON contract.
 *
 * Both modes use the production invoice-v2 draft contract: optional job, customer,
 * dates, line items, discount, tax, terms, and notes. Edit prefills from the enriched
 * detail query; the server recomputes totals authoritatively in either mode. The
 * submit body is built through the shared `buildInvoiceDraftPayload` helper
 * (`components/InvoiceLineItemsEditor.js`) so create and edit never drift.
 *
 * CREATE mode (27.1-04 — GAP-a/GAP-b/D-08) is a full-draft, job-optional form: an
 * Info section (optional job, issue/due dates, the pre-filled-but-editable invoice
 * number), a Customer section, a Line Items section (discount/tax), an optional
 * attachment, and Notes. It POSTs the structured full-draft JSON body
 * `POST /api/v1/invoices` parses (`lineItems` present ⇒ the server takes the
 * full-draft branch, which does NOT require a job — money-spine Decision #8). The
 * invoice-number field is pre-filled from `GET /api/v1/invoices/next-number` on
 * mount as a CONVENIENCE ONLY: the server still generates/validates a unique
 * number when the field is left blank on save, so a failed suggestion fetch never
 * blocks the form.
 *
 * EDIT mode (21-11) is a full editor matching prod invoice-v2 semantics: line
 * items, discount, tax, customer info, terms, and notes. It prefills from the
 * enriched detail query and submits the same structured JSON body the api-host
 * `PUT /api/v1/invoices/:id` parses; the server recomputes totals authoritatively.
 *
 * The submit goes through `apiFetch` (the CSRF double-submit token is baked in
 * for non-safe methods). Every input has a visible `<label htmlFor>` (A11Y-01);
 * the primary CTA is specific ("Save Invoice" create / "Save Changes" edit),
 * never a bare "Save".
 *
 * ATTACHMENT (27.1-05 — GAP-b/GAP-c): attachment is a SEPARATE endpoint from
 * create/update (`POST`/`DELETE /api/v1/invoices/:id/attachment`, bytes to R2
 * via `saveUpload` — never FS). CREATE is a two-step: the invoice is created
 * first, and only if that succeeds is the optional file then POSTed to the new id
 * (`uploadInvoiceAttachment`, `useInvoiceQuery.ts`). If the create succeeds but
 * the attachment POST fails, the created invoice is NEVER lost — a warning is
 * shown with a manual link to the invoice instead of losing the reference to a
 * silently-abandoned redirect. EDIT reuses the same detail-page mutations
 * (`useUploadInvoiceAttachmentMutation` / `useDeleteInvoiceAttachmentMutation`)
 * for replace/remove parity, plus the same client-side file validation
 * (`validateInvoiceAttachmentFile`) mirroring the server's limits.
 */

const editRouteApi = getRouteApi('/invoices/$id/edit');

/** Route wrapper for `/invoices/$id/edit` — reads the id param and prefills. */
export function InvoiceEditRoute() {
  const { id } = editRouteApi.useParams();
  return <InvoiceForm mode="edit" invoiceId={Number(id)} />;
}

export interface InvoiceFormProps {
  mode: 'create' | 'edit';
  /** Present in edit mode only (the `/invoices/$id/edit` route param). */
  invoiceId?: number;
}

interface DraftFormState {
  jobId: string;
  /** Pre-filled from `GET /api/v1/invoices/next-number` on create; always
   *  editable and NEVER authoritative — the server generates/validates the
   *  real number. */
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: string;
  termsText: string;
  publicNotes: string;
  internalNotes: string;
  discountType: InvoiceDiscountType;
  discountValue: string;
  taxRatePercent: string;
  lineItems: InvoiceLineRow[];
}

function blankDraftState(): DraftFormState {
  return {
    jobId: '',
    invoiceNumber: '',
    issueDate: '',
    dueDate: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    termsText: '',
    publicNotes: '',
    internalNotes: '',
    discountType: 'none',
    discountValue: '',
    taxRatePercent: '0',
    lineItems: [newInvoiceLineRow()],
  };
}

const INPUT_CLASS =
  'h-11 rounded-md border border-input bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

export function InvoiceForm({ mode, invoiceId }: InvoiceFormProps) {
  return <InvoiceDraftForm mode={mode} invoiceId={invoiceId} />;
}

function InvoiceDraftForm({ mode, invoiceId }: InvoiceFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fmt = useMoneyFormatters();

  const jobFieldId = useId();
  const numberId = useId();
  const issueId = useId();
  const dueId = useId();
  const nameId = useId();
  const emailId = useId();
  const phoneId = useId();
  const addressId = useId();
  const termsId = useId();
  const publicNotesId = useId();
  const internalNotesId = useId();
  const attachmentInputId = useId();

  const isCreate = mode === 'create';
  const isEdit = mode === 'edit' && typeof invoiceId === 'number' && invoiceId > 0;
  const detailQuery = useInvoiceQuery(invoiceId ?? 0, isEdit);
  const jobsQuery = useJobsQuery(true);
  // CREATE-only: suggested next invoice number (27.1-04). Never fired in edit
  // mode — the edit form already has a real, server-assigned number.
  const nextNumberQuery = useNextInvoiceNumberQuery(isCreate);
  // CREATE-only: the workspace default tax rate from Settings. A convenience read on the
  // same footing as the number suggestion — a Manager without settings.view gets a 403 and
  // the form keeps today's 0%. Never fired in edit mode: an existing invoice owns its rate.
  const settingsDefaults = useSettingsDefaults(isCreate);
  // Reuse the SAME detail-page mutations (parity, DRY) — the endpoints are
  // shared (`POST`/`DELETE /api/v1/invoices/:id/attachment`); both already
  // invalidate the detail query on success so the form's "current attachment"
  // view stays consistent with server truth after either action. Harmless to
  // construct in create mode too (invoiceId is 0 and the mutation is never
  // triggered there).
  const uploadAttachment = useUploadInvoiceAttachmentMutation(invoiceId ?? 0);
  const deleteAttachment = useDeleteInvoiceAttachmentMutation(invoiceId ?? 0);

  const [form, setForm] = useState<DraftFormState>(blankDraftState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachmentFileError, setAttachmentFileError] = useState<string | null>(null);
  const [attachmentDeleteOpen, setAttachmentDeleteOpen] = useState(false);
  const prefilled = useRef(false);
  const numberPrefilled = useRef(false);
  const taxPrefilled = useRef(false);
  // Once the operator picks a due date by hand, the payment-terms fill below stops following the
  // issue date.
  const dueTouched = useRef(false);

  // CREATE-only state: the file is staged locally and only uploaded AFTER the
  // invoice itself is created (two-step, see module doc). `attachmentWarning`
  // + `createdInvoiceId` are set only on the partial-failure path (create
  // succeeded, attachment upload did not) — the money-spine prohibition is
  // that the created invoice must never be silently lost, so we stop the
  // auto-redirect and hand the user an explicit link to it instead of racing
  // a toast against navigation.
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentWarning, setAttachmentWarning] = useState<string | null>(null);
  const [createdInvoiceId, setCreatedInvoiceId] = useState<number | null>(null);

  // Prefill once from the detail query (guarded so a background refetch never
  // clobbers in-progress edits).
  useEffect(() => {
    if (!isEdit || prefilled.current) return;
    const invoice = detailQuery.data?.invoice;
    if (!invoice) return;
    prefilled.current = true;
    setForm({
      jobId: invoice.jobId != null ? String(invoice.jobId) : '',
      invoiceNumber: invoice.invoiceNumber ?? '',
      issueDate: invoice.dateIssued ?? '',
      dueDate: invoice.dueDate ?? '',
      customerName: invoice.customerName ?? '',
      customerEmail: invoice.customerEmail ?? '',
      customerPhone: invoice.customerPhone ?? '',
      customerAddress: invoice.customerAddress ?? '',
      termsText: invoice.termsText ?? '',
      publicNotes: invoice.publicNotes ?? invoice.notes ?? '',
      internalNotes: invoice.internalNotes ?? '',
      discountType: invoice.discountType ?? 'none',
      discountValue: invoice.discountValue != null ? String(invoice.discountValue) : '',
      taxRatePercent: typeof invoice.taxRate === 'number' ? String(invoice.taxRate) : '0',
      lineItems: (invoice.lineItems ?? []).length
        ? (invoice.lineItems ?? []).map((li) => ({
            description: li.description ?? '',
            quantity: String(li.quantity ?? ''),
            unit: li.unit ?? '',
            unitPrice: String(li.unitPrice ?? ''),
          }))
        : [newInvoiceLineRow()],
    });
  }, [isEdit, detailQuery.data]);

  // Pre-fill the suggested number ONCE on create, and only if the operator has
  // not already typed one — a slow/late-resolving fetch must never clobber
  // in-progress input. The field stays fully editable either way.
  useEffect(() => {
    if (!isCreate || numberPrefilled.current) return;
    const suggested = nextNumberQuery.data?.invoiceNumber;
    if (!suggested) return;
    numberPrefilled.current = true;
    setForm((prev) => (prev.invoiceNumber ? prev : { ...prev, invoiceNumber: suggested }));
  }, [isCreate, nextNumberQuery.data]);

  // Pre-fill the Settings default tax rate ONCE on create, and only while the field still
  // holds the blank default — a late-resolving read must never clobber a rate the operator
  // already typed. Edit mode never reaches here (the read is disabled there).
  useEffect(() => {
    if (!isCreate || taxPrefilled.current) return;
    const rate = settingsDefaults?.defaultTaxRate;
    if (rate === undefined) return;
    taxPrefilled.current = true;
    setForm((prev) =>
      prev.taxRatePercent === '0' ? { ...prev, taxRatePercent: String(rate) } : prev,
    );
  }, [isCreate, settingsDefaults]);

  // On create, the due date follows the issue date by the company's payment terms (the rule the
  // server applies to a blank due date) until the operator sets one. Without the terms (no
  // settings.view) it stays blank and the server fills it, rather than guessing Net 30 here.
  useEffect(() => {
    if (!isCreate || dueTouched.current) return;
    const days = settingsDefaults?.invoiceTermsDays;
    if (days === undefined || !/^\d{4}-\d{2}-\d{2}$/.test(form.issueDate)) return;
    const due = new Date(`${form.issueDate}T00:00:00Z`);
    due.setUTCDate(due.getUTCDate() + days);
    const dueDate = due.toISOString().slice(0, 10);
    setForm((prev) => (prev.dueDate === dueDate ? prev : { ...prev, dueDate }));
  }, [isCreate, form.issueDate, settingsDefaults]);

  const termsKnown = isCreate && settingsDefaults?.invoiceTermsDays !== undefined;

  const set = <K extends keyof DraftFormState>(key: K, value: DraftFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // CREATE: stage the file locally (validated up front); it is only uploaded
  // once the invoice exists (see onSubmit's two-step).
  // EDIT: replace immediately — POST straight over the existing attachment
  // (the server deletes the superseded file — prod parity, `api.ts:1522-1531`).
  const onSelectAttachment = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    if (isCreate) {
      if (!file) {
        setAttachmentFile(null);
        setAttachmentFileError(null);
        return;
      }
      const validationError = validateInvoiceAttachmentFile(file);
      if (validationError) {
        // Reject before it ever reaches state — the server's own limits are
        // re-checked on upload regardless (T-27.1-02), this is just fast UX.
        event.target.value = '';
        setAttachmentFile(null);
        setAttachmentFileError(
          validationError === 'tooLarge'
            ? t('invoices:form.attachmentTooLarge')
            : t('invoices:form.attachmentInvalidType'),
        );
        return;
      }
      setAttachmentFileError(null);
      setAttachmentFile(file);
      return;
    }

    // Reset the input so re-selecting the same filename still fires change.
    event.target.value = '';
    if (!file) return;
    const validationError = validateInvoiceAttachmentFile(file);
    if (validationError) {
      setAttachmentFileError(
        validationError === 'tooLarge'
          ? t('invoices:form.attachmentTooLarge')
          : t('invoices:form.attachmentInvalidType'),
      );
      return;
    }
    setAttachmentFileError(null);
    const formData = new FormData();
    formData.set('attachment', file);
    uploadAttachment.mutate(formData);
  };

  const confirmDeleteAttachment = () => {
    setAttachmentDeleteOpen(false);
    deleteAttachment.mutate();
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (isCreate) {
      setAttachmentWarning(null);
      setCreatedInvoiceId(null);
    }
    setSubmitting(true);
    const body = buildInvoiceDraftPayload(form);

    try {
      const response = await apiFetch(
        isEdit ? `/api/v1/invoices/${invoiceId}` : '/api/v1/invoices',
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      const result = (await response.json().catch(() => null)) as {
        ok?: boolean;
        error?: string;
        invoice?: { id: number };
      } | null;
      if (!response.ok || !result || result.ok === false) {
        setError(
          result?.error === 'invoice_locked'
            ? t('invoices:form.locked')
            : (result?.error ?? t('invoices:form.genericError')),
        );
        return;
      }
      if (isEdit) {
        await queryClient.invalidateQueries({ queryKey: invoiceQueryKey(invoiceId as number) });
      }
      const savedId = isEdit ? invoiceId : result.invoice?.id;
      if (!savedId) {
        setError(t('invoices:form.genericError'));
        return;
      }

      // Two-step (RESEARCH-resolved, Open Question 2): the invoice must exist
      // before an attachment can target it. If the optional file fails to
      // upload here, the invoice itself is already safely created — stop the
      // auto-redirect and surface the exact link instead of silently losing it.
      if (isCreate && attachmentFile) {
        try {
          await uploadInvoiceAttachment(savedId, attachmentFile);
        } catch {
          setAttachmentWarning(t('invoices:form.attachmentUploadFailedWarning'));
          setCreatedInvoiceId(savedId);
          return;
        }
      }

      void navigate({ to: '/invoices/$id', params: { id: String(savedId) } });
    } catch {
      setError(t('invoices:form.genericError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (isEdit && detailQuery.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (isEdit && (detailQuery.isError || !detailQuery.data?.invoice)) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('invoices:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('invoices:error.body')}</p>
      </section>
    );
  }

  const jobs = jobsQuery.data?.jobs ?? [];
  const currentAttachmentFilename = detailQuery.data?.invoice?.attachmentFilename ?? null;

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display font-semibold text-3xl text-foreground">
          {t(mode === 'create' ? 'invoices:form.createHeading' : 'invoices:form.editHeading')}
        </h1>
      </header>

      {error ? (
        <div
          role="alert"
          className="mb-4 max-w-3xl rounded-md border border-destructive/30 bg-destructive/10 p-4 text-destructive text-sm"
        >
          {error}
        </div>
      ) : null}

      {isCreate && attachmentWarning && createdInvoiceId ? (
        <div
          role="alert"
          className="mb-4 flex max-w-3xl flex-wrap items-center gap-3 rounded-md border border-warning/30 bg-warning/10 p-4 text-sm text-warning"
        >
          <p className="flex-1">{attachmentWarning}</p>
          <Button asChild variant="secondary" size="sm">
            <Link to="/invoices/$id" params={{ id: String(createdInvoiceId) }}>
              {t('invoices:form.viewInvoiceCta')}
            </Link>
          </Button>
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="flex max-w-3xl flex-col gap-4">
        <section className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-semibold text-foreground text-xl">
            {t('invoices:form.infoHeading')}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1 sm:col-span-2">
              <label htmlFor={jobFieldId} className="font-medium text-foreground text-sm">
                {t('invoices:form.job')}
              </label>
              <select
                id={jobFieldId}
                value={form.jobId}
                onChange={(e) => set('jobId', e.target.value)}
                className={INPUT_CLASS}
              >
                <option value="">{t('invoices:form.jobPlaceholder')}</option>
                {jobs.map((j) => (
                  <option key={j.id} value={String(j.id)}>
                    {j.job_name || `#${j.id}`}
                    {j.client_name ? ` — ${j.client_name}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1 sm:col-span-2">
              <label htmlFor={numberId} className="font-medium text-foreground text-sm">
                {t('invoices:form.invoiceNumber')}
              </label>
              <input
                id={numberId}
                type="text"
                value={form.invoiceNumber}
                onChange={(e) => set('invoiceNumber', e.target.value)}
                placeholder={isCreate ? 'INV-…' : 'Generated when blank'}
                className={`${INPUT_CLASS} font-mono`}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={issueId} className="font-medium text-foreground text-sm">
                {t('invoices:form.issueDate')}
              </label>
              <input
                id={issueId}
                type="date"
                value={form.issueDate}
                onChange={(e) => set('issueDate', e.target.value)}
                required
                className={`${INPUT_CLASS} font-mono`}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={dueId} className="font-medium text-foreground text-sm">
                {t('invoices:form.dueDate')}
              </label>
              <input
                id={dueId}
                type="date"
                value={form.dueDate}
                onChange={(e) => {
                  dueTouched.current = true;
                  set('dueDate', e.target.value);
                }}
                // Blank is allowed only when the terms are known, so the server can fill it the
                // same way; otherwise it stays required, as it always was.
                required={!termsKnown}
                aria-describedby={termsKnown ? `${dueId}-hint` : undefined}
                className={`${INPUT_CLASS} font-mono`}
              />
              {termsKnown ? (
                <p id={`${dueId}-hint`} className="text-muted-foreground text-xs">
                  {t('invoices:form.dueDateHint')}
                </p>
              ) : null}
            </div>
          </div>
        </section>

        <section className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-semibold text-foreground text-xl">
            {t('invoices:form.customerHeading')}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label htmlFor={nameId} className="font-medium text-foreground text-sm">
                {t('invoices:form.customerName')}
              </label>
              <input
                id={nameId}
                type="text"
                value={form.customerName}
                onChange={(e) => set('customerName', e.target.value)}
                required
                className={INPUT_CLASS}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={emailId} className="font-medium text-foreground text-sm">
                {t('invoices:form.customerEmail')}
              </label>
              <input
                id={emailId}
                type="email"
                value={form.customerEmail}
                onChange={(e) => set('customerEmail', e.target.value)}
                className={INPUT_CLASS}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={phoneId} className="font-medium text-foreground text-sm">
                {t('invoices:form.customerPhone')}
              </label>
              <input
                id={phoneId}
                type="tel"
                value={form.customerPhone}
                onChange={(e) => set('customerPhone', e.target.value)}
                className={INPUT_CLASS}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={addressId} className="font-medium text-foreground text-sm">
                {t('invoices:form.customerAddress')}
              </label>
              <input
                id={addressId}
                type="text"
                value={form.customerAddress}
                onChange={(e) => set('customerAddress', e.target.value)}
                className={INPUT_CLASS}
              />
            </div>
          </div>
        </section>

        <section className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-semibold text-foreground text-xl">
            {t('invoices:form.lineItemsHeading')}
          </h2>
          <InvoiceLineItemsEditor
            rows={form.lineItems}
            onRowsChange={(rows) => set('lineItems', rows)}
            discountType={form.discountType}
            discountValue={form.discountValue}
            taxRatePercent={form.taxRatePercent}
            onDiscountTypeChange={(value) => set('discountType', value)}
            onDiscountValueChange={(value) => set('discountValue', value)}
            onTaxRateChange={(value) => set('taxRatePercent', value)}
            fmt={fmt}
          />
        </section>

        <section className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-semibold text-foreground text-xl">
            {t('invoices:form.attachmentHeading')}
          </h2>
          {isCreate ? (
            <div className="flex flex-col gap-1">
              <label htmlFor={attachmentInputId} className="font-medium text-foreground text-sm">
                {t('invoices:form.attachmentOptionalLabel')}
              </label>
              <input
                id={attachmentInputId}
                type="file"
                accept={INVOICE_ATTACHMENT_ACCEPT}
                onChange={onSelectAttachment}
                className="text-foreground text-sm file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:font-medium file:text-secondary-foreground file:text-sm"
              />
              <p className="text-muted-foreground text-xs">{t('invoices:form.attachmentHint')}</p>
              {attachmentFileError ? (
                <p role="alert" className="text-destructive text-xs">
                  {attachmentFileError}
                </p>
              ) : attachmentFile ? (
                <p className="text-foreground text-xs">{attachmentFile.name}</p>
              ) : null}
            </div>
          ) : currentAttachmentFilename ? (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium text-foreground">
                  {t('invoices:form.currentAttachmentLabel')}:
                </span>
                <span className="text-foreground">{currentAttachmentFilename}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    void openCredentialed(invoiceAttachmentUrl(invoiceId as number)).catch((err) =>
                      console.error('invoice attachment view failed', err),
                    )
                  }
                >
                  {t('invoices:detail.viewAttachment')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={deleteAttachment.isPending}
                  onClick={() => setAttachmentDeleteOpen(true)}
                >
                  {t('invoices:form.removeAttachment')}
                </Button>
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor={attachmentInputId} className="font-medium text-foreground text-sm">
                  {t('invoices:form.replaceAttachmentLabel')}
                </label>
                <input
                  id={attachmentInputId}
                  type="file"
                  accept={INVOICE_ATTACHMENT_ACCEPT}
                  onChange={onSelectAttachment}
                  disabled={uploadAttachment.isPending}
                  className="text-foreground text-sm file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:font-medium file:text-secondary-foreground file:text-sm"
                />
                <p className="text-muted-foreground text-xs">{t('invoices:form.attachmentHint')}</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <p className="mb-1 text-muted-foreground text-sm">
                {t('invoices:form.noAttachmentYet')}
              </p>
              <label htmlFor={attachmentInputId} className="font-medium text-foreground text-sm">
                {t('invoices:form.attachmentOptionalLabel')}
              </label>
              <input
                id={attachmentInputId}
                type="file"
                accept={INVOICE_ATTACHMENT_ACCEPT}
                onChange={onSelectAttachment}
                disabled={uploadAttachment.isPending}
                className="text-foreground text-sm file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:font-medium file:text-secondary-foreground file:text-sm"
              />
              <p className="text-muted-foreground text-xs">{t('invoices:form.attachmentHint')}</p>
            </div>
          )}
          {!isCreate && attachmentFileError ? (
            <p role="alert" className="mt-2 text-destructive text-xs">
              {attachmentFileError}
            </p>
          ) : null}
          {!isCreate && (uploadAttachment.isError || deleteAttachment.isError) ? (
            <p role="alert" className="mt-2 text-destructive text-sm">
              {t('invoices:detail.attachmentError')}
            </p>
          ) : null}
        </section>

        <section className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-semibold text-foreground text-xl">
            {t('invoices:form.notesHeading')}
          </h2>
          <div className="grid grid-cols-1 gap-4">
            <div className="flex flex-col gap-1">
              <label htmlFor={termsId} className="font-medium text-foreground text-sm">
                {t('invoices:form.terms')}
              </label>
              <RichTextEditor
                id={termsId}
                value={form.termsText}
                onChange={(html) => set('termsText', html)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={publicNotesId} className="font-medium text-foreground text-sm">
                {t('invoices:form.publicNotes')}
              </label>
              <RichTextEditor
                id={publicNotesId}
                value={form.publicNotes}
                onChange={(html) => set('publicNotes', html)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={internalNotesId} className="font-medium text-foreground text-sm">
                {t('invoices:form.internalNotes')}
              </label>
              <textarea
                id={internalNotesId}
                value={form.internalNotes}
                onChange={(e) => set('internalNotes', e.target.value)}
                rows={2}
                className="rounded-md border border-input bg-background px-3 py-2 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              />
            </div>
          </div>
        </section>

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting
              ? t('invoices:form.saving')
              : t(mode === 'create' ? 'invoices:form.submitCreate' : 'invoices:form.submitEdit')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              isEdit
                ? void navigate({ to: '/invoices/$id', params: { id: String(invoiceId) } })
                : void navigate({ to: '/invoices' })
            }
          >
            {t('invoices:form.cancel')}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={attachmentDeleteOpen}
        heading={t('invoices:detail.deleteAttachmentHeading')}
        body={t('invoices:detail.deleteAttachmentBody')}
        confirmLabel={t('invoices:detail.deleteAttachmentConfirm')}
        cancelLabel={t('invoices:detail.deleteAttachmentCancel')}
        confirmPending={deleteAttachment.isPending}
        onConfirm={confirmDeleteAttachment}
        onCancel={() => setAttachmentDeleteOpen(false)}
      />
    </div>
  );
}
